<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card flat class="pa-4">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="6">
              <h2 class="headline mb-2">App Management Dashboard</h2>
              <div class="subtitle-1 grey--text">
                Centralized control panel for all management modules.
              </div>
            </v-col>
            <!-- Removed Quick Jump and Collapse -->
          </v-row>
          <v-divider class="my-4" />

          <!-- Even Smaller Management Cards (Icon-only, Tooltip, Responsive) -->
          <v-row class="mini-cards-row">
            <v-col
              v-for="module in modules"
              :key="module.key"
              cols="3"
              sm="2"
              md="1"
              class="pa-1 d-flex justify-center"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="
                      selectedModule === module.key
                        ? 'primary'
                        : 'grey lighten-4'
                    "
                    depressed
                    @click="selectModule(module.key)"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon
                      :color="
                        selectedModule === module.key ? 'white' : 'primary'
                      "
                      size="26"
                    >
                      {{ module.icon }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ module.title }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card flat class="pa-4">
          <v-card-title>
            <v-icon left>{{ currentModule.icon }}</v-icon>
            <span class="headline">{{ currentModule.title }}</span>
            <v-spacer />
          </v-card-title>
          <v-divider />
          <v-card-text>
            <component
              :is="currentModule.component"
              @settings-saved="handleSettingsSaved"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ActiveUsers from "@/components/management/activeUsers.vue";
import LogDashboard from "@/components/management/logDashboard.vue";
import ManagementSettings from "@/components/management/management-settings.vue";
import ManagementClaimSettings from "@/components/management/ManagementClaimSettings.vue";
import {
  VAlert,
  VProgressCircular,
  VList,
  VListItem,
  VListItemContent,
  VListItemTitle,
  VListItemSubtitle,
  VListItemAvatar,
  VListItemAction,
  VIcon,
  VChip,
} from "vuetify/lib";
import EventBus from "@/common/EventBus";

// --- Placeholder Component ---
const PlaceholderComponent = (msg) => ({
  functional: true,
  components: { VAlert },
  render(h) {
    return h(
      VAlert,
      {
        props: {
          type: "info",
          border: "left",
          "colored-border": true,
        },
      },
      [msg]
    );
  },
});

export default {
  name: "ManagementDashboard",
    components: {
      ActiveUsers,
      LogDashboard,
      ManagementSettings,
      ManagementClaimSettings,
    VAlert,
    VProgressCircular,
    VList,
    VListItem,
    VListItemContent,
    VListItemTitle,
    VListItemSubtitle,
    VListItemAvatar,
    VListItemAction,
    VIcon,
    VChip,
  },
  data() {
    return {
      selectedModule: "activeUsers",
      modules: [
        {
          key: "activeUsers",
          title: "Active Users",
          shortTitle: "Active",
          icon: "mdi-account-multiple-check",
          description: "View currently active users.",
          component: "ActiveUsers",
        },
        {
          key: "document",
          title: "Document Management",
          shortTitle: "Docs",
          icon: "mdi-folder",
          description: "Manage documents and storage.",
          component: PlaceholderComponent(
            "Document Management module coming soon."
          ),
        },
        {
          key: "status",
          title: "Status Monitoring",
          shortTitle: "Status",
          icon: "mdi-server",
          description: "Monitor app, server, and ISP status.",
          component: PlaceholderComponent(
            "Status Monitoring module coming soon."
          ),
        },
        {
          key: "users",
          title: "User Management",
          shortTitle: "Users",
          icon: "mdi-account-group",
          description: "Manage users, roles, and permissions.",
          component: PlaceholderComponent(
            "User Management module coming soon."
          ),
        },
        {
          key: "logs",
          title: "Log Dashboard",
          shortTitle: "Logs",
          icon: "mdi-history",
          description: "View and manage application logs.",
          component: "LogDashboard",
        },

        {
          key: "notifications",
          title: "Notification Center",
          shortTitle: "Notify",
          icon: "mdi-bell",
          description: "Manage notifications and alerts.",
          component: PlaceholderComponent(
            "Notification Center module coming soon."
          ),
        },
        {
          key: "integrations",
          title: "API Keys & Integrations",
          shortTitle: "API",
          icon: "mdi-api",
          description: "Manage API keys and integrations.",
          component: PlaceholderComponent(
            "API Keys & Integrations module coming soon."
          ),
        },
        {
          key: "settings",
          title: "App Settings",
          shortTitle: "Settings",
          icon: "mdi-cog",
          description: "Configure application settings.",
          component: "ManagementSettings",
        },
        {
          key: "claimSettings",
          title: "Claim Settings",
          shortTitle: "Claims",
          icon: "mdi-file-document-multiple",
          description: "Configure claim module timing parameters.",
          component: "ManagementClaimSettings",
        },
      ],
    };
  },
  computed: {
    currentModule() {
      return (
        this.modules.find((m) => m.key === this.selectedModule) ||
        this.modules[0]
      );
    },
  },
  methods: {
    selectModule(key) {
      this.selectedModule = key;
    },
    handleSettingsSaved(newSettings) {
      // Broadcast to appBar or other listeners
      EventBus.emit("settings-saved", newSettings);
    },
  },
};
</script>

<style scoped>
.management-module-card {
  transition: box-shadow 0.2s, border-color 0.2s;
  border: 2px solid transparent;
}
.management-module-card.active-card {
  border-color: #1976d2;
  box-shadow: 0 4px 24px rgba(25, 118, 210, 0.12);
}
.headline {
  font-weight: 600;
  font-size: 1.3em;
}
.mini-cards-row {
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0;
}

.collapsed-bar {
  min-height: 48px;
  background: #f5f6fa;
  border-radius: 6px;
  border: 1px dashed #b0bec5;
  padding: 8px 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
